import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  Box,
  CircularProgress,
} from '@mui/material';
import { payoutsService } from '@/services';
import { useNotification } from '@/contexts/NotificationContext';

const MINUTES_TO_SECONDS = 60;

function parsePositiveIntegerMinutes(value: string): { ok: true; minutes: number } | { ok: false; message: string } {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { ok: false, message: 'Заполните все поля' };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
    return { ok: false, message: 'Введите целое число минут не менее 1' };
  }
  return { ok: true, minutes: n };
}

function secondsToDisplayMinutes(seconds: number): string {
  return String(Math.max(1, Math.ceil(seconds / MINUTES_TO_SECONDS)));
}

export type PaymentSystemOutSettingsDialogMode = 'create' | 'edit';

interface PaymentSystemOutSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  paymentSystemId: string | null;
  /** create — POST после создания ПС; edit — GET+PUT по существующей ПС */
  mode: PaymentSystemOutSettingsDialogMode;
  /** Создание настроек — FINANCE_WALLETS_CREATE; изменение — FINANCE_WALLETS_UPDATE */
  canSave: boolean;
  onSuccess?: () => void;
}

const PaymentSystemOutSettingsDialog: React.FC<PaymentSystemOutSettingsDialogProps> = ({
  open,
  onClose,
  paymentSystemId,
  mode,
  canSave,
  onSuccess,
}) => {
  const { showNotification } = useNotification();
  const [poolMinutes, setPoolMinutes] = useState('1');
  const [acceptedMinutes, setAcceptedMinutes] = useState('1');
  const [inProgressMinutes, setInProgressMinutes] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !paymentSystemId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      if (mode === 'create') {
        setPoolMinutes('1');
        setAcceptedMinutes('1');
        setInProgressMinutes('1');
        setSubmitting(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      setSubmitting(false);
      try {
        const data = await payoutsService.getPaymentSystemPayoutOutSettingsByPaymentSystem(paymentSystemId);
        if (cancelled) return;
        setPoolMinutes(secondsToDisplayMinutes(data.pool_ttl_seconds));
        setAcceptedMinutes(secondsToDisplayMinutes(data.accepted_ttl_seconds));
        setInProgressMinutes(secondsToDisplayMinutes(data.in_progress_ttl_seconds));
      } catch (err: any) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          showNotification('Настройки выплат OUT для этой ПС ещё не созданы', 'error');
          onClose();
        } else {
          const detail = err.response?.data?.detail;
          const msg =
            (Array.isArray(detail) && detail[0]?.msg) ||
            (typeof detail === 'string' ? detail : null) ||
            err.message ||
            'Не удалось загрузить настройки';
          showNotification(typeof msg === 'string' ? msg : 'Не удалось загрузить настройки', 'error');
          onClose();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [open, paymentSystemId, mode, onClose]);

  const handleSubmit = async () => {
    if (!paymentSystemId || !canSave) return;

    const pool = parsePositiveIntegerMinutes(poolMinutes);
    const accepted = parsePositiveIntegerMinutes(acceptedMinutes);
    const inProgress = parsePositiveIntegerMinutes(inProgressMinutes);
    if (!pool.ok) {
      showNotification(pool.message, 'error');
      return;
    }
    if (!accepted.ok) {
      showNotification(accepted.message, 'error');
      return;
    }
    if (!inProgress.ok) {
      showNotification(inProgress.message, 'error');
      return;
    }

    const ttlPayload = {
      pool_ttl_seconds: pool.minutes * MINUTES_TO_SECONDS,
      accepted_ttl_seconds: accepted.minutes * MINUTES_TO_SECONDS,
      in_progress_ttl_seconds: inProgress.minutes * MINUTES_TO_SECONDS,
    };

    setSubmitting(true);
    try {
      if (mode === 'create') {
        await payoutsService.createPaymentSystemPayoutOutSettings({
          payment_system_id: paymentSystemId,
          ...ttlPayload,
        });
        showNotification('Настройки выплат OUT сохранены', 'success');
      } else {
        await payoutsService.updatePaymentSystemPayoutOutSettingsByPaymentSystem(paymentSystemId, ttlPayload);
        showNotification('Настройки выплат OUT обновлены', 'success');
      }
      onSuccess?.();
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const msg =
        (Array.isArray(detail) && detail[0]?.msg) ||
        (typeof detail === 'string' ? detail : null) ||
        err.message ||
        'Ошибка при сохранении настроек';
      showNotification(typeof msg === 'string' ? msg : 'Ошибка при сохранении настроек', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    mode === 'create' ? 'Настройки выплат OUT' : 'Редактировать настройки выплат OUT';

  return (
    <Dialog open={open} onClose={submitting || loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label="Pool TTL"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={poolMinutes}
              onChange={(e) => setPoolMinutes(e.target.value)}
              helperText="Минуты (целое число ≥ 1)"
              disabled={submitting}
            />
            <TextField
              fullWidth
              required
              label="Accepted TTL"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={acceptedMinutes}
              onChange={(e) => setAcceptedMinutes(e.target.value)}
              helperText="Минуты (целое число ≥ 1)"
              disabled={submitting}
            />
            <TextField
              fullWidth
              required
              label="In progress TTL"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={inProgressMinutes}
              onChange={(e) => setInProgressMinutes(e.target.value)}
              helperText="Минуты (целое число ≥ 1)"
              disabled={submitting}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting || loading}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loading || !paymentSystemId || !canSave}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentSystemOutSettingsDialog;
